import type { MouseEvent } from 'react';
import { Link } from '@mui/material';
import { Star } from '@mui/icons-material';

import type { AdMediaType, AdModel } from '@/types';
import { priceFormat } from '@/utils';
import { TopAgentBadge } from '../../uiComponents/TopAgentBadge/TopAgentBadge';

import styles from './AdsListItem.module.scss';
import { AdsListItemFavoriteButton } from './AdsListItemFavoriteButton';
import ImgArrayLeftBtn from './svg/imgArrayLeftBtn.svg';
import ImgArrayRightBtn from './svg/imgArrayRightBtn.svg';
import ItemIconImages from './svg/itemIconImages.svg';
import ItemIconUpdated from './svg/itemIconUpdated.svg';
import ItemIconVideo from './svg/itemIconVideo.svg';

interface AdsListItemMediaProps {
  addCommission: AdModel['addCommission'];
  currencyCommission: AdModel['currencyCommission'];
  favorite: boolean;
  hasVideo: boolean;
  id: string;
  imageIndex: number;
  images: AdMediaType[];
  imgUrl: string;
  isCreatorRestricted: boolean;
  isSale: boolean;
  onFavoriteClick: (event: MouseEvent<SVGSVGElement>) => void;
  onImageIndexChange: (event: MouseEvent<HTMLElement>, newIndex: number) => void;
  onSelectionClick: (event: MouseEvent<SVGSVGElement>) => void;
  parsedUpdateDate: string;
  showSelectionAction: boolean;
  showTopAgent: boolean;
  title: string;
  transactionDate: string;
  transactionType: AdModel['transactionType'];
}

export const AdsListItemMedia = ({
  addCommission,
  currencyCommission,
  favorite,
  hasVideo,
  id,
  imageIndex,
  images,
  imgUrl,
  isCreatorRestricted,
  isSale,
  onFavoriteClick,
  onImageIndexChange,
  onSelectionClick,
  parsedUpdateDate,
  showSelectionAction,
  showTopAgent,
  title,
  transactionDate,
  transactionType,
}: AdsListItemMediaProps) => (
  <Link href={`/ads/${id}`} title={title} className={styles.itemImg}>
    {images.length > 0 && <img src={imgUrl} alt={title} />}
    {images.length > 1 && (
      <div className={styles.sliderControls}>
        <img
          src={ImgArrayLeftBtn}
          alt='Prev image'
          onClick={(event) => onImageIndexChange(event, imageIndex - 1)}
        />
        <img
          src={ImgArrayRightBtn}
          alt='Next image'
          onClick={(event) => onImageIndexChange(event, imageIndex + 1)}
        />
      </div>
    )}
    <div className={styles.itemImgInfo}>
      <div className={styles.itemUpdatedDate}>
        <img src={ItemIconUpdated} alt='' />
        {parsedUpdateDate}
      </div>
      {isSale && transactionType && (
        <div className={styles.itemTransactionDate}>
          <p>{transactionDate}</p>
        </div>
      )}
      <div className={styles.itemImgIcons}>
        <div className={styles.itemImgIconsImages}>
          <img src={ItemIconImages} alt='Image' />
          {images.length}
        </div>
        {hasVideo && <img src={ItemIconVideo} alt='' />}
        <div className={styles.itemImgIconsImages}>
          <AdsListItemFavoriteButton favorite={favorite} onClick={onFavoriteClick} />
        </div>
        {showSelectionAction && (
          <div className={styles.itemImgIconsImages}>
            <Star
              onClick={onSelectionClick}
              color='secondary'
              style={{
                fill: 'none',
                stroke: '#FF9800',
                strokeWidth: 2,
              }}
            />
          </div>
        )}
      </div>
    </div>
    {addCommission && (
      <div className={styles.extraCommission}>
        <p>
          Extra commission: {priceFormat(addCommission)} {currencyCommission}
        </p>
      </div>
    )}
    {showTopAgent && (
      <div
        className={`${styles.topAgentOverlay} ${
          isCreatorRestricted ? styles.blurText : styles.text
        }`}
        data-testid='top-agent-overlay'
      >
        <TopAgentBadge size='large' />
      </div>
    )}
  </Link>
);
